import React, { useEffect } from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import { gql, useMutation, Reference, StoreObject } from '@apollo/client';
import { useForm, SubmitHandler, DefaultValues } from 'react-hook-form';

import { ADD_CAUSAL, EDIT_CAUSAL, DEL_CAUSAL } from '../queries/resources';
import { Resources_resources_causales as Causal } from '../graph-types/Resources';
import { CausalInput } from '../graph-types/globalTypes';
import { AddCausal, AddCausalVariables } from '../graph-types/AddCausal';
import { UpdateCausal, UpdateCausalVariables } from '../graph-types/UpdateCausal';
import { DeleteCausal, DeleteCausalVariables } from '../graph-types/DeleteCausal';
import { Resources_resources as Resources } from '../graph-types/Resources';

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      marginRight: 'auto',
    },
  })
);

type FormValues = {
  initials: string;
  causal: string;
};

const getCausalData = (id: string, data: FormValues): CausalInput => {
  return {
    id,
    initials: data.initials,
    causal: data.causal,
  };
};

type TypeOfSubmit = 'Save' | 'Delete';

const useCausalForm = (causal: Causal | null, defaultValues: DefaultValues<FormValues>, resources: Resources) => {
  const form = useForm<FormValues>({ defaultValues });

  const [deleteCausal] = useMutation<DeleteCausal, DeleteCausalVariables>(DEL_CAUSAL, {
    update: (cache, { data }) => {
      cache.modify({
        id: cache.identify((resources as unknown) as StoreObject),
        fields: {
          causales(existingCausals = [], { readField }) {
            if (!data) {
              return existingCausals;
            }

            return existingCausals.filter((causalRef: Reference) => data.deleteCausal !== readField('id', causalRef));
          },
        },
      });
    },
  });

  const [addCausal] = useMutation<AddCausal, AddCausalVariables>(ADD_CAUSAL, {
    update: (cache, { data }) => {
      cache.modify({
        id: cache.identify((resources as unknown) as StoreObject),
        fields: {
          causales(existingCausals = []) {
            if (!data) {
              return existingCausals;
            }

            const causalData = getCausalData(data.addCausal.id, form.getValues());
            const newCausalRef = cache.writeFragment({
              data: {
                ...causalData,
                __typename: 'Causal',
              },
              fragment: gql`
                fragment NewCausal on Causal {
                  id
                  initials
                  causal
                }
              `,
            });
            return [...existingCausals, newCausalRef];
          },
        },
      });
    },
  });

  const [editCausal] = useMutation<UpdateCausal, UpdateCausalVariables>(EDIT_CAUSAL);

  const onSubmit = (typeOfSubmit: TypeOfSubmit, cb: () => void): SubmitHandler<FormValues> => async (data) => {
    switch (typeOfSubmit) {
      case 'Save': {
        if (causal) {
          await editCausal({
            variables: {
              causal: getCausalData(causal.id, data),
            },
          });
        } else {
          await addCausal({
            variables: {
              causal: getCausalData('', data),
            },
          });
        }
        break;
      }
      case 'Delete': {
        if (causal) {
          await deleteCausal({ variables: { causalId: causal.id } });
        }
        break;
      }
    }

    cb();
  };

  return {
    onSubmit: (typeOfSubmit: TypeOfSubmit, cb: () => void) => form.handleSubmit(onSubmit(typeOfSubmit, cb)),
    ...form,
  };
};

const getDefaultValues = (causal: Causal | null): DefaultValues<FormValues> => {
  const defaultValues: DefaultValues<FormValues> = {
    initials: causal?.initials ?? '',
    causal: causal?.causal ?? '',
  };

  return defaultValues;
};

export const CausalDialog: React.FC<{
  open: boolean;
  handleClose: () => void;
  causal: Causal | null;
  resources: Resources;
}> = ({ handleClose, causal, open, resources }) => {
  const classes = useStyles();

  const { register, formState, onSubmit, reset } = useCausalForm(causal, getDefaultValues(causal), resources);

  const { errors } = formState;

  useEffect(() => {
    reset(getDefaultValues(causal));
  }, [reset, causal, open]);

  const onClose = (): void => {
    handleClose();
    reset(getDefaultValues(null));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={'sm'}>
      <form
        onSubmit={onSubmit('Save', () => {
          handleClose();
        })}
      >
        <DialogTitle id="form-dialog-title">Crear Causal</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            id="causal"
            label="Nombre"
            type="text"
            variant="outlined"
            inputProps={register('causal', { required: true })}
            error={!!errors.causal}
          />
          <TextField
            fullWidth
            margin="dense"
            id="initials"
            label="Iniciales"
            type="text"
            variant="outlined"
            inputProps={register('initials')}
          />
        </DialogContent>
        <DialogActions>
          {causal && (
            <Button
              classes={classes}
              onClick={() =>
                onSubmit('Delete', () => {
                  handleClose();
                })()
              }
              color="secondary"
              disabled={formState.isSubmitting}
            >
              Borrar
            </Button>
          )}
          <Button onClick={onClose} color="primary" disabled={formState.isSubmitting}>
            Cerrar
          </Button>
          <Button type="submit" color="primary" disabled={formState.isSubmitting}>
            Guardar
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
